import {
    Box,
    Button,
    FormControl,
    InputLabel,
    Paper,
    Select,
    Stack,
} from "@mui/material";

import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
    0% { background-position: -468px 0; }
    100% { background-position: 468px 0; }
`;

const ORANGE = "#ED6C02";
const ORANGE_HOVER = "#C75A02";
const CARD_SHADOW =
    "0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)";

export const PageWrapper = styled(Box)`
    display: inline-block;
    width: 100%;
    padding: 30px;
    color: #0f172a;
    position: relative;
    z-index: 0;
    @media (max-width: 900px) {
        padding: 20px;
    }
    @media (max-width: 600px) {
        padding: 16px;
    }
`;

export const HeaderRow = styled(Stack)`
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    margin-bottom: 24px;
`;

export const PageTitle = styled(Box)`
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #0f172a;
    line-height: 1.15;
    @media (max-width: 600px) {
        font-size: 22px;
    }
`;

export const PageSubtitle = styled(Box)`
    font-size: 13px;
    color: #64748b;
    margin-top: 4px;
    max-width: 480px;
    @media (max-width: 600px) {
        font-size: 12px;
    }
`;

export const FilterBar = styled(Stack)`
    flex-direction: row;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
`;

export const FilterCard = styled(Paper)`
    && {
        background: #ffffff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 10px;
        box-shadow: none;
        padding: 22px 24px;
        overflow: visible;
        @media (max-width: 900px) {
            padding: 16px 18px;
        }
        @media (max-width: 600px) {
            padding: 12px 14px;
        }
    }
`;

export const FilterRow = styled(Box)`
    display: grid;
    grid-template-columns:
        minmax(180px, 1.2fr)
        minmax(180px, 1fr)
        minmax(180px, 1fr)
        minmax(180px, 1fr)
        168px;
    align-items: center;
    gap: 16px;
    width: 100%;
    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        gap: 12px;
    }
`;

export const FilterField = styled(Box)<{
    $grow?: number;
    $fullOnMobile?: boolean;
}>`
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 6px;
    position: relative;
`;

export const FilterLabel = styled(Box)`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: #94a3b8;
    text-transform: uppercase;
`;

export const FilterControl = styled.button<{ $active?: boolean }>`
    width: 100%;
    text-align: left;
    background: #ffffff;
    border: 1px solid ${(p) => (p.$active ? ORANGE : "#E2E8F0")};
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 500;
    color: #0f172a;
    min-height: 42px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    transition:
        border-color 0.15s ease,
        background 0.15s ease;
    &:hover {
        border-color: ${ORANGE};
        background: #fff7ed;
    }
    &:focus {
        outline: none;
        border-color: ${ORANGE};
    }
`;

export const PrimaryButton = styled(Button)`
    && {
        text-transform: none;
        background: ${ORANGE};
        color: #ffffff;
        border-radius: 6px;
        padding: 8px 18px;
        font-weight: 600;
        font-size: 13px;
        min-height: 44px;
        box-shadow: none;
        gap: 6px;
        &:hover {
            background: ${ORANGE_HOVER};
        }
        &:disabled {
            background: #f8c9a1;
            color: #ffffff;
        }
    }
`;

export const GroupByWrapper = styled(Stack)`
    flex-direction: row;
    align-items: center;
    gap: 10px;
    padding: 4px 10px 4px 14px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    border-radius: 6px;
    min-height: 38px;
`;

export const StyledFormControl = styled(FormControl)`
    && {
        width: 100%;

        & .MuiOutlinedInput-notchedOutline {
            border-color: #e5e5ea;
        }
        &:hover .MuiOutlinedInput-notchedOutline {
            border-color: ${ORANGE};
        }
        & .Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${ORANGE};
        }
        & .MuiOutlinedInput-root {
            height: 40px;
            box-sizing: border-box;
        }
    }
`;

export const StyledInputLabel = styled(InputLabel)`
    &.MuiInputLabel-root {
        font-family: "Nunito", sans-serif;
    }

    &.Mui-focused {
        color: ${ORANGE};
    }
`;

export const StyledSelect = styled(Select)`
    font-family: "Nunito", sans-serif;
    font-size: 13px;
    font-weight: 600;
    height: 40px;
    box-sizing: border-box;

    & .MuiSelect-select {
        display: flex;
        align-items: center;
        height: 40px;
        min-height: 0 !important;
        padding-top: 0;
        padding-bottom: 0;
        box-sizing: border-box;
        overflow: hidden;
    }
`;

export const SelectValueText = styled(Box)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1 1 auto;
`;

export const SelectActionsRow = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 12px;
    border-bottom: 1px solid #e5e5ea;
    cursor: default;
    outline: none;

    &:hover,
    &:focus,
    &.Mui-selected {
        background-color: transparent;
    }
`;

export const SelectActionButton = styled.button`
    border: none;
    background: none;
    padding: 2px 4px;
    font-family: "Nunito", sans-serif;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
    color: ${ORANGE};
    cursor: pointer;

    &:hover:not(:disabled) {
        color: ${ORANGE_HOVER};
        text-decoration: underline;
    }

    &:disabled {
        color: #b0b0b8;
        cursor: default;
    }
`;

export const DateInputRow = styled(Stack)`
    && {
        & .MuiOutlinedInput-root {
            height: 40px;
            box-sizing: border-box;
            font-family: "Nunito", sans-serif;
            font-size: 13px;
            font-weight: 600;
        }
        & .MuiOutlinedInput-notchedOutline {
            border-color: #e5e5ea;
        }
        & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
            border-color: ${ORANGE};
        }
        & .Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${ORANGE};
        }
    }
`;

export const GroupByLabel = styled(Box)`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: #64748b;
    text-transform: uppercase;
`;

export const GroupByToggle = styled.button<{ $active: boolean }>`
    border: none;
    background: transparent;
    font-size: 13px;
    font-weight: ${(p) => (p.$active ? 600 : 500)};
    color: ${(p) => (p.$active ? ORANGE : "#64748B")};
    cursor: pointer;
    padding: 6px 8px;
    border-radius: 4px;
    &:hover {
        color: ${ORANGE};
    }
`;

export const MetricCard = styled(Paper)`
    && {
        padding: 18px 24px;
        border-radius: 10px;
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: none;
        background: #ffffff;
        display: flex;
        flex-direction: column;
        gap: 10px;
        height: 100%;
        font-family: "Nunito", sans-serif;
        transition: transform 0.2s ease;
        &:hover {
            transform: translateY(-2px);
        }
        @media (max-width: 600px) {
            padding: 14px 16px;
            gap: 8px;
        }
    }
`;

export const MetricLabel = styled(Box)`
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.6);
    text-transform: uppercase;
    font-family: "Nunito", sans-serif;
`;

export const MetricValue = styled(Box)`
    font-size: clamp(18px, 2.4vw, 32px);
    font-weight: 700;
    color: #0f172a;
    line-height: 1.15;
    font-family: "Nunito", sans-serif;
    @media (max-width: 600px) {
        font-size: 26px;
    }
`;

export const IconBadge = styled(Box)<{ $bg: string; $color?: string }>`
    border-radius: 8px;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color ?? "#0F172A"};
    flex-shrink: 0;
`;

export const ProgressTrack = styled(Box)`
    width: 100%;
    height: 6px;
    border-radius: 999px;
    background: #f1f5f9;
    overflow: hidden;
`;

export const ProgressFill = styled(Box)<{ $width: number; $color: string }>`
    width: ${(p) => Math.max(0, Math.min(100, p.$width))}%;
    height: 100%;
    background: ${(p) => p.$color};
    border-radius: 999px;
    transition: width 0.4s ease;
`;

export const SectionCard = styled(Paper)<{ $noBorder?: boolean }>`
    && {
        padding: 22px 24px;
        border-radius: 8px;
        border: 1px solid ${(p) => (p.$noBorder ? "transparent" : "#eef2f6")};
        background: #ffffff;
        @media (max-width: 900px) {
            padding: 18px 18px;
        }
        @media (max-width: 600px) {
            padding: 14px 14px;
        }
    }
`;

export const SectionTitle = styled(Box)`
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
`;

export const SectionSubtitle = styled(Box)`
    font-size: 13px;
    color: #64748b;
    margin-top: 2px;
`;

export const StatusChip = styled(Box)<{ $bg: string; $color: string }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    width: fit-content;
`;

export const ChipRemoveBtn = styled.button`
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    line-height: 0;
    color: inherit;
    opacity: 0.7;
    &:hover {
        opacity: 1;
    }
`;

export const SourceCard = styled(Paper)<{ $accent?: string }>`
    && {
        padding: 16px 18px;
        border-radius: 8px;
        border: 1px solid #eef2f6;
        background: #ffffff;
        display: flex;
        flex-direction: column;
        gap: 10px;
        height: 100%;
    }
    img {
        width: 24px;
    }
`;

export const TableWrap = styled(Box)`
    overflow-x: auto;
    border: 1px solid #eef2f6;
    border-radius: 8px;
    margin-top: 12px;
    @media (max-width: 768px) {
        display: none;
    }
`;

export const MobileCardList = styled(Box)`
    display: none;
    flex-direction: column;
    gap: 10px;
    margin-top: 12px;
    @media (max-width: 768px) {
        display: flex;
    }
`;

export const ConnMobileCard = styled(Paper)`
    && {
        padding: 14px 14px;
        border-radius: 8px;
        border: 1px solid #eef2f6;
        box-shadow: ${CARD_SHADOW};
        background: #ffffff;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
`;

export const ConnMobileMetric = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    flex: 1;
`;

export const ConnMobileMetricLabel = styled(Box)`
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
`;

export const ConnMobileMetricValue = styled(Box)<{ $color?: string }>`
    font-size: 16px;
    font-weight: 700;
    color: ${(p) => p.$color ?? "#0F172A"};
`;

export const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    min-width: 880px;
    th {
        text-align: left;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #64748b;
        padding: 12px 16px;
        background: #f8fafc;
        border-bottom: 1px solid #eef2f6;
    }
    td {
        padding: 0 16px;
        height: 56px;
        border-bottom: 1px solid #f1f5f9;
        color: #0f172a;
    }
    tr:last-child td {
        border-bottom: none;
    }
    tbody tr:hover {
        background: #f9fafb;
        cursor: pointer;
    }
`;

export const IconChip = styled(Box)<{ $bg: string; $color: string }>`
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
`;

export const SkeletonBlock = styled(Box)<{ $h?: number }>`
    height: ${(p) => p.$h ?? 20}px;
    border-radius: 8px;
    background: linear-gradient(to right, #f1f5f9 8%, #e2e8f0 18%, #f1f5f9 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.4s linear infinite forwards;
`;

export const ErrorBanner = styled(Stack)`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
`;

export const EmptyStateWrap = styled(Stack)`
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
    gap: 12px;
`;

export const DropdownPanel = styled(Paper)`
    && {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        z-index: 40;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
        padding: 8px;
        min-width: 220px;
    }
`;

export const DropdownItem = styled.button<{ $active?: boolean }>`
    width: 100%;
    border: none;
    background: ${(p) => (p.$active ? "#FFF7ED" : "transparent")};
    font-size: 13px;
    color: ${(p) => (p.$active ? ORANGE : "#0F172A")};
    padding: 8px 10px;
    border-radius: 4px;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    &:hover {
        background: #fff7ed;
        color: ${ORANGE};
    }
`;
